import { getTypeText } from "../../../../core/models/entities/BaseLogo";
import {
  OrderLine,
  OrderLineDesign,
  OrderLineLogo,
} from "../../../../core/models/entities/Order";
import { ReactNode, useState } from "react";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";

const getPriceString = (price?: number, currency?: string) =>
  price ? `${currency || "DKK"} ${price.toFixed(2)}` : "";

const useOrderLinePrice = (
  orderline: OrderLine,
  showCustomerPrices?: boolean
) => {
  const { role } = useProfileContext();

  // When showCustomerPrices is true, always show the adjusted price.
  // Otherwise, Vendors (Role.Vendor = 1) should see the vendor price (orderline.price)
  // and Customers (Role.EndUser = 0) and others should see customer price (orderline.adjustedPrice)
  const displayUnitPrice =
    showCustomerPrices || role !== Role.Vendor
      ? orderline.adjustedUnitPrice
      : orderline.unitPrice;

  const displayPrice = displayUnitPrice;

  return { displayPrice, displayUnitPrice };
};

export interface OrderLineHideColumns {
  displayNo?: boolean;
  image?: boolean;
  itemNumber?: boolean;
  title?: boolean;
  type?: boolean;
  unitPrice?: boolean;
  quantity?: boolean;
  price?: boolean;
  subTotal?: boolean;
}

export const OrderLineHeaders = ({ hide }: { hide?: OrderLineHideColumns }) => {
  return (
    <tr>
      {!hide?.displayNo && <th className="w-1/12 text-left px-2">No.</th>}
      {!hide?.image && <th className="w-1/12 text-left px-2">Image</th>}
      {!hide?.itemNumber && <th className="w-1/12 text-left px-2">Item No.</th>}
      {!hide?.title && <th className="w-2/12 text-left px-2">Title</th>}
      {!hide?.type && <th className="w-1/12 text-left px-2">Type</th>}
      {!hide?.unitPrice && (
        <th className="w-1/12 text-right px-2">Price Per Piece</th>
      )}
      {!hide?.quantity && <th className="w-1/12 text-right px-2">QTY</th>}
      {!hide?.price && <th className="w-1/12 text-right px-2">QTY Price</th>}
      {!hide?.subTotal && <th className="w-1/12 text-right px-2">Total</th>}
    </tr>
  );
};

export interface SharedOrderLineDataProps {
  displayNo?: string;
  hide?: OrderLineHideColumns;
  bold?: boolean;
  currency?: string;
  isFirstItem?: boolean;
  showCustomerPrices?: boolean;
  onRowClick?: () => void;
  leadingNote?: ReactNode;
  trailingNote?: ReactNode;
}

export interface OrderLineDataProps extends SharedOrderLineDataProps {
  title: string;
  imageSrc?: string;
  itemNumber?: string;
  type?: string;
  price?: number;
  unitPrice?: number;
  quantity?: number;
  minQuantity?: number;
  subTotal?: number;
}

export default function OrderLineData({
  displayNo,
  imageSrc,
  itemNumber: sku,
  title,
  type,
  price,
  unitPrice,
  currency,
  quantity,
  minQuantity,
  subTotal,
  hide,
  bold,
  isFirstItem,
  onRowClick,
  leadingNote,
  trailingNote,
}: OrderLineDataProps) {
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [imageRect, setImageRect] = useState<DOMRect | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleMouseEnter = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setImageRect(rect);
    setIsImageHovered(true);
    // Start animation after a brief delay to ensure the modal is rendered
    setTimeout(() => setIsAnimating(true), 10);
  };

  const handleMouseLeave = () => {
    setIsAnimating(false);
    // Keep the modal visible during the scale-down animation
    setTimeout(() => setIsImageHovered(false), 150);
  };

  const actualPrice = price || 0,
    actualUnitPrice = unitPrice || 0,
    actualQuantity = quantity || 0,
    actualSubTotal = subTotal || actualQuantity * actualPrice;

  const cleanedDisplayNo = displayNo?.replace(/\s+-\s+/g, "-") || "";
  const shouldHideLineNo = cleanedDisplayNo.split("-").length >= 3;

  // Determine if this is a main item (e.g., "01") or sub item (e.g., "01-1", "01-2")
  const isMainItem = displayNo && !displayNo.includes("-");
  const isSubItem = displayNo && displayNo.includes("-");

  const mainItemClass = isMainItem ? "font-bold bg-white px-2" : "";
  const subItemClass = isSubItem
    ? type === "AdditionalFee" ||
      sku === "From Storage" ||
      type === "Additional Fee"
      ? "bg-gray-100 px-2"
      : "font-bold bg-gray-100 px-2"
    : "";

  const rowHeightClass = isMainItem ? "h-18" : isSubItem ? "h-10" : "h-10";

  const clickableClass = onRowClick ? "cursor-pointer hover:bg-gray-50" : "";

  return (
    <tr
      className={`min-h-32 ${rowHeightClass} ${clickableClass}`}
      onClick={onRowClick}
    >
      {!hide?.displayNo && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {leadingNote}
          {!shouldHideLineNo ? cleanedDisplayNo : ""}
        </td>
      )}
      {!hide?.image && (
        <td className={`place-items-left ${mainItemClass}${subItemClass}`}>
          {imageSrc && (
            <>
              <img
                src={imageSrc}
                className="my-2 object-contain max-h-[50px] max-w-[80px] bg-white rounded hover:border hover:p-1 transition-all duration-150 ease-out"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
              {isImageHovered &&
                imageRect &&
                (() => {
                  const padding = 5; // Corresponds to p-1 (4px) and border (1px)
                  const targetSize = 350; // The target size in pixels for the longest side of the zoomed image

                  const newWidth = imageRect.width + padding * 2;
                  const newHeight = imageRect.height + padding * 2;
                  const newLeft = imageRect.left - padding;
                  const newTop = imageRect.top - padding;

                  const longestSide = Math.max(newWidth, newHeight);
                  const scale = targetSize / longestSide;

                  return (
                    <div className="fixed inset-0 pointer-events-none z-[9999]">
                      <div
                        className="transition-all duration-200 ease-out flex justify-center items-center"
                        style={{
                          position: "absolute",
                          left: newLeft,
                          top: newTop,
                          width: newWidth,
                          height: newHeight,
                          transformOrigin: isFirstItem
                            ? "top left"
                            : "center left",
                          transform: isAnimating
                            ? `scale(${scale})`
                            : "scale(1)",
                        }}
                      >
                        <img
                          src={imageSrc}
                          className="object-contain bg-white rounded border shadow-2xl"
                          style={{
                            width: "100%",
                            height: "100%",
                            padding: `${padding}px`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </td>
      )}
      {!hide?.itemNumber && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {sku || ""}
        </td>
      )}
      {!hide?.title && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {title === "Film pr. farve" ? "Film charge per color" : title || ""}
          {trailingNote}
        </td>
      )}
      {!hide?.type && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {type || ""}
        </td>
      )}
      {!hide?.unitPrice && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {getPriceString(actualUnitPrice, currency)}
        </td>
      )}
      {!hide?.quantity && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {actualQuantity || ""}
        </td>
      )}
      {!hide?.price && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {getPriceString(actualPrice, currency)}
        </td>
      )}
      {!hide?.subTotal && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {getPriceString(actualSubTotal, currency) +
            (minQuantity ? ` (${minQuantity})` : "")}
        </td>
      )}
    </tr>
  );
}

export interface BaseOrderLineDataProps extends SharedOrderLineDataProps {
  orderline: OrderLine;
}
export function BaseOrderLineData({
  displayNo,
  orderline,
  hide,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: BaseOrderLineDataProps) {
  const { displayPrice, displayUnitPrice } = useOrderLinePrice(
    orderline,
    showCustomerPrices
  );

  return (
    <OrderLineData
      displayNo={displayNo ?? orderline.displayNo}
      hide={hide}
      itemNumber={orderline.product.productNo}
      title={orderline.product.productName}
      type={orderline.type}
      quantity={orderline.quantity}
      price={displayPrice}
      unitPrice={displayUnitPrice}
      currency={currency}
      isFirstItem={isFirstItem}
      onRowClick={onRowClick}
      leadingNote={leadingNote}
      trailingNote={trailingNote}
    />
  );
}

export interface DesignOrderLineDataProps extends BaseOrderLineDataProps {
  orderline: OrderLineDesign;
}
export function DesignOrderLineData({
  displayNo,
  orderline,
  hide,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: DesignOrderLineDataProps) {
  const { displayPrice, displayUnitPrice } = useOrderLinePrice(
    orderline,
    showCustomerPrices
  );

  return (
    <OrderLineData
      bold={true}
      displayNo={displayNo ?? orderline.displayNo}
      hide={hide}
      imageSrc={orderline.displayImage}
      itemNumber={orderline.product.productNo}
      title={orderline.product.productName}
      type={orderline.product.productType}
      quantity={orderline.quantity}
      price={displayPrice}
      unitPrice={displayUnitPrice}
      currency={currency}
      isFirstItem={isFirstItem}
      onRowClick={onRowClick}
      leadingNote={leadingNote}
      trailingNote={trailingNote}
    />
  );
}

export interface LogoOrderLineDataProps extends BaseOrderLineDataProps {
  orderline: OrderLineLogo;
}
export function LogoOrderLineData({
  displayNo,
  orderline,
  hide,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: LogoOrderLineDataProps) {
  const { displayPrice, displayUnitPrice } = useOrderLinePrice(
    orderline,
    showCustomerPrices
  );

  return (
    <OrderLineData
      displayNo={displayNo ?? orderline.displayNo}
      hide={hide}
      imageSrc={orderline.displayImage}
      itemNumber={orderline.product.productNo}
      title={orderline.product.productName}
      // baseLogo is absent while the order-detail screens render the
      // placeholder row from the order-list cache (list lines carry only
      // product data, no logo object) - getTypeText would crash on it.
      // Fall back to the generic product type until the real fetch lands.
      type={orderline.baseLogo ? getTypeText(orderline.baseLogo) : orderline.type}
      quantity={orderline.quantity}
      price={displayPrice}
      unitPrice={displayUnitPrice}
      currency={currency}
      isFirstItem={isFirstItem}
      onRowClick={onRowClick}
      leadingNote={leadingNote}
      trailingNote={trailingNote}
    />
  );
}

export interface GenericOrderLineDataProps extends SharedOrderLineDataProps {
  orderline: OrderLine;
}
export const GenericOrderLine = ({
  hide,
  displayNo,
  orderline,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: GenericOrderLineDataProps) => {
  switch (orderline.product.productType) {
    case "Design":
      return (
        <DesignOrderLineData
          displayNo={displayNo || orderline.displayNo}
          hide={hide}
          orderline={orderline as OrderLineDesign}
          currency={currency}
          isFirstItem={isFirstItem}
          showCustomerPrices={showCustomerPrices}
          onRowClick={onRowClick}
          leadingNote={leadingNote}
          trailingNote={trailingNote}
        />
      );
    case "Logo":
      return (
        <LogoOrderLineData
          displayNo={displayNo || orderline.displayNo}
          hide={hide}
          orderline={orderline as OrderLineLogo}
          currency={currency}
          isFirstItem={isFirstItem}
          showCustomerPrices={showCustomerPrices}
          onRowClick={onRowClick}
          leadingNote={leadingNote}
          trailingNote={trailingNote}
        />
      );
    default:
      return (
        <BaseOrderLineData
          displayNo={displayNo || orderline.displayNo}
          hide={hide}
          orderline={orderline}
          currency={currency}
          isFirstItem={isFirstItem}
          showCustomerPrices={showCustomerPrices}
          onRowClick={onRowClick}
          leadingNote={leadingNote}
          trailingNote={trailingNote}
        />
      );
  }
};
